import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  ClientFilesView,
  FileItem,
  FileRef,
  FirmClientRow,
  FirmFilesPage,
  FirmFilesQuery,
  FirmFilesSummary,
  FolderItem,
  FolderTemplate,
  FolderVisibility,
  TemplateFolder,
  UploadFilesRequest,
} from "../features/files/files.types";
import { filesSlice } from "./FilesSlice";

type Count = { count: number };

const TAGS = [
  "ClientFiles",
  "WorkFiles",
  "FirmFiles",
  "FolderTemplates",
] as const;

/**
 * Documents (backend api/files): client folders, the Documents page, the
 * recycle bin and folder templates. Changes also refresh the older
 * Files card / work Files lists (filesSlice).
 */
export const documentsSlice = createApi({
  reducerPath: "documentsSlice",
  tagTypes: TAGS,
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => {
    const refreshOld = async (
      _arg: unknown,
      {
        dispatch,
        queryFulfilled,
      }: {
        dispatch: (action: unknown) => void;
        queryFulfilled: Promise<unknown>;
      },
    ) => {
      try {
        await queryFulfilled;
      } finally {
        dispatch(filesSlice.util.invalidateTags(["ContactFiles", "WorkFile"]));
      }
    };
    const changes = [...TAGS].filter((t) => t !== "FolderTemplates");

    return {
      getClientFiles: build.query<ClientFilesView, string>({
        query: (customerId) => ({ url: `api/files/clients/${customerId}` }),
        providesTags: ["ClientFiles"],
      }),
      getWorkFilesList: build.query<FileItem[], string>({
        query: (workId) => ({ url: `api/files/work/${workId}` }),
        providesTags: ["WorkFiles"],
      }),
      createFolder: build.mutation<
        FolderItem,
        {
          customerId: string;
          parentId?: string | null;
          name: string;
          visibility?: FolderVisibility;
        }
      >({
        query: (body) => ({ url: "api/files/folders", method: "POST", body }),
        invalidatesTags: changes,
      }),
      updateFolder: build.mutation<
        boolean,
        {
          id: string;
          name?: string;
          visibility?: FolderVisibility;
          parentId?: string | null;
          moveToTop?: boolean;
        }
      >({
        query: ({ id, ...body }) => ({
          url: `api/files/folders/${id}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: changes,
      }),
      deleteFolder: build.mutation<Count, string>({
        query: (id) => ({ url: `api/files/folders/${id}`, method: "DELETE" }),
        invalidatesTags: changes,
        onQueryStarted: refreshOld,
      }),
      moveFiles: build.mutation<
        Count,
        { items: FileRef[]; folderId: string | null }
      >({
        query: (body) => ({ url: "api/files/move", method: "POST", body }),
        invalidatesTags: changes,
        onQueryStarted: refreshOld,
      }),
      linkFilesToWork: build.mutation<
        Count,
        { items: FileRef[]; workId: string | null }
      >({
        query: (body) => ({ url: "api/files/link-work", method: "POST", body }),
        invalidatesTags: changes,
        onQueryStarted: refreshOld,
      }),
      renameFile: build.mutation<boolean, FileRef & { name: string }>({
        query: (body) => ({ url: "api/files/rename", method: "POST", body }),
        invalidatesTags: changes,
        onQueryStarted: refreshOld,
      }),
      deleteFiles: build.mutation<Count, FileRef[]>({
        query: (items) => ({
          url: "api/files/delete",
          method: "POST",
          body: { items },
        }),
        invalidatesTags: changes,
        onQueryStarted: refreshOld,
      }),
      markFilesSeen: build.mutation<Count, FileRef[]>({
        query: (items) => ({
          url: "api/files/seen",
          method: "POST",
          body: { items },
        }),
        invalidatesTags: changes,
      }),
      restoreFiles: build.mutation<Count, FileRef[]>({
        query: (items) => ({
          url: "api/files/restore",
          method: "POST",
          body: { items },
        }),
        invalidatesTags: changes,
        onQueryStarted: refreshOld,
      }),
      deleteFilesForever: build.mutation<Count, FileRef[]>({
        query: (items) => ({
          url: "api/files/delete-forever",
          method: "POST",
          body: { items },
        }),
        invalidatesTags: changes,
      }),
      uploadFiles: build.mutation<FileItem[], UploadFilesRequest>({
        query: ({ files, ...fields }) => {
          const body = new FormData();
          files.forEach((file) => body.append("files", file));
          if (fields.customerId) body.append("customerId", fields.customerId);
          if (fields.folderId) body.append("folderId", fields.folderId);
          if (fields.workId) body.append("workId", fields.workId);
          body.append("share", String(!!fields.share));
          body.append("notify", String(!!fields.notify));
          if (fields.message) body.append("message", fields.message);
          return { url: "api/files/upload", method: "POST", body };
        },
        invalidatesTags: changes,
        onQueryStarted: refreshOld,
      }),
      saveEmailAttachment: build.mutation<
        FileItem,
        {
          customerId: string;
          folderId?: string | null;
          workId?: string | null;
          messageId: string;
          attachmentId: string;
          fileName: string;
        }
      >({
        query: (body) => ({
          url: "api/files/from-email",
          method: "POST",
          body,
        }),
        invalidatesTags: changes,
        onQueryStarted: refreshOld,
      }),
      getFirmFiles: build.query<FirmFilesPage, FirmFilesQuery>({
        query: (params) => ({ url: "api/files/firm", params }),
        providesTags: ["FirmFiles"],
      }),
      getFirmFileClients: build.query<
        FirmClientRow[],
        { orgId: string; mine?: boolean; search?: string }
      >({
        query: (params) => ({ url: "api/files/firm/clients", params }),
        providesTags: ["FirmFiles"],
      }),
      getFirmFilesSummary: build.query<
        FirmFilesSummary,
        { orgId: string; mine?: boolean }
      >({
        query: (params) => ({ url: "api/files/firm/summary", params }),
        providesTags: ["FirmFiles"],
      }),
      getFolderTemplates: build.query<FolderTemplate[], string>({
        query: (orgId) => ({ url: "api/files/templates", params: { orgId } }),
        providesTags: ["FolderTemplates"],
      }),
      canManageFolderTemplates: build.query<boolean, string>({
        query: (orgId) => ({
          url: "api/files/templates/can-manage",
          params: { orgId },
        }),
      }),
      saveFolderTemplate: build.mutation<
        FolderTemplate,
        {
          id?: string;
          orgId: string;
          name: string;
          isDefault: boolean;
          addYearlyFolders: boolean;
          folders: TemplateFolder[];
        }
      >({
        query: ({ id, ...body }) => ({
          url: id ? `api/files/templates/${id}` : "api/files/templates",
          method: id ? "PUT" : "POST",
          body,
        }),
        invalidatesTags: ["FolderTemplates"],
      }),
      deleteFolderTemplate: build.mutation<boolean, string>({
        query: (id) => ({ url: `api/files/templates/${id}`, method: "DELETE" }),
        invalidatesTags: ["FolderTemplates"],
      }),
      applyFolderTemplate: build.mutation<
        { clients: number; foldersAdded: number },
        { id: string; customerIds?: string[]; allClients?: boolean }
      >({
        query: ({ id, ...body }) => ({
          url: `api/files/templates/${id}/apply`,
          method: "POST",
          body,
        }),
        invalidatesTags: changes,
      }),
    };
  },
});

export const {
  useGetClientFilesQuery,
  useGetWorkFilesListQuery,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
  useMoveFilesMutation,
  useLinkFilesToWorkMutation,
  useRenameFileMutation,
  useDeleteFilesMutation,
  useMarkFilesSeenMutation,
  useRestoreFilesMutation,
  useDeleteFilesForeverMutation,
  useUploadFilesMutation,
  useSaveEmailAttachmentMutation,
  useGetFirmFilesQuery,
  useGetFirmFileClientsQuery,
  useGetFirmFilesSummaryQuery,
  useGetFolderTemplatesQuery,
  useCanManageFolderTemplatesQuery,
  useSaveFolderTemplateMutation,
  useDeleteFolderTemplateMutation,
  useApplyFolderTemplateMutation,
} = documentsSlice;

// The server address has no trailing "/" ("https://dev.procharted.com"), so
// join with one: without it the browser asked "dev.procharted.comapi" and
// showed "Failed to fetch" (Roadmap 1844).
const apiUrl = (path: string) =>
  `${devConfig.getServerBaseUrlForEnv().replace(/\/+$/, "")}/${path.replace(
    /^\/+/,
    "",
  )}`;

const apiGet = async (path: string): Promise<{ url?: string | null }> => {
  const response = await fetch(apiUrl(path), {
    headers: { Authorization: getAuthHeader().Authorization },
  });
  if (!response.ok)
    throw new Error((await response.text()) || "This file couldn't be opened.");
  return response.json();
};

/** A link to look at a file (also marks a client upload as seen). */
export const getFilePreviewUrl = async (file: FileRef) =>
  (await apiGet(`api/files/preview?kind=${file.kind}&id=${file.id}`)).url ||
  null;

/** A 15-minute link that downloads the file under its own name. */
export const getFileDownloadUrl = async (file: FileRef) =>
  (await apiGet(`api/files/download?kind=${file.kind}&id=${file.id}`)).url ||
  null;

/** Downloads several files and/or whole folders as one zip. */
export const downloadZip = async (
  items: FileRef[],
  folderIds: string[] = [],
) => {
  const response = await fetch(apiUrl("api/files/zip"), {
    method: "POST",
    headers: {
      Authorization: getAuthHeader().Authorization,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ items, folderIds }),
  });
  if (!response.ok) {
    throw new Error(
      (await response.text()) || "The download didn't work. Please try again.",
    );
  }
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const name = match ? decodeURIComponent(match[1]) : "Files.zip";
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};
